import React, { useEffect, useState } from 'react';
import { SwipeableDrawer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import ForumIcon from '@mui/icons-material/Forum';
import LinkIcon from '@mui/icons-material/Link';

import { getColor } from '../../Graph/utils';
import { getEdgeCategoryColor, getEdgeCategoryLabel } from '../../edgeCategories';

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';
}

function formatDate(date) {
  if (!date) return 'Date unknown';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function PersonPhoto({ person, className = '' }) {
  const [didImageFail, setDidImageFail] = useState(false);
  const imageURL = person?.imageURL;

  useEffect(() => {
    setDidImageFail(false);
  }, [imageURL]);

  return (
    <div
      className={`graph-detail-avatar ${className}`}
      style={{ '--detail-accent': getColor(person?._id) }}
      aria-hidden={!person?.name}
    >
      {imageURL && !didImageFail ? (
        <img src={imageURL} alt={person?.name || ''} onError={() => setDidImageFail(true)} />
      ) : (
        <span>{getInitials(person?.name)}</span>
      )}
    </div>
  );
}

function getQuestionPerson(question, person1, person2) {
  if (person1?._id === question?.personId) return person1;
  if (person2?._id === question?.personId) return person2;
  return null;
}

export const EdgeDrawer = ({
  selectedEdge,
  person1,
  person2,
  open,
  toggleDrawer,
  onPersonClick,
}) => {
  const categoryColor = getEdgeCategoryColor(selectedEdge?.category);
  const hasInterviewAnswers = Boolean(selectedEdge?.interviewQuestions?.length);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={toggleDrawer(false)}
      onOpen={toggleDrawer(true)}
      swipeAreaWidth={selectedEdge ? 48 : 0}
      disableSwipeToOpen={false}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          height: 'min(86dvh, calc(100dvh - env(safe-area-inset-top, 0px)))',
          overflow: 'visible',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <section
        className="graph-detail-sheet graph-edge-sheet"
        style={{
          '--detail-accent': categoryColor,
          '--detail-left': getColor(person1?._id),
          '--detail-right': getColor(person2?._id),
          '--detail-accent-soft': `color-mix(in srgb, ${categoryColor} 24%, transparent)`,
        }}
        aria-label={person1 && person2 ? `View connection between ${person1.name} and ${person2.name}` : 'View connection'}
      >
        <div className="graph-detail-puller" />
        <button type="button" className="graph-detail-close" onClick={toggleDrawer(false)} aria-label="Close connection details">
          <CloseIcon fontSize="small" />
        </button>

        <header className="graph-edge-hero">
          <button
            type="button"
            className="graph-edge-person"
            onClick={() => onPersonClick(person1)}
            disabled={!person1}
          >
            <PersonPhoto person={person1} className="graph-detail-avatar--edge" />
            <strong>{person1?.name || 'Unknown'}</strong>
          </button>
          <div className="graph-edge-connector" aria-hidden="true">
            <span />
            <LinkIcon fontSize="small" />
            <span />
          </div>
          <button
            type="button"
            className="graph-edge-person"
            onClick={() => onPersonClick(person2)}
            disabled={!person2}
          >
            <PersonPhoto person={person2} className="graph-detail-avatar--edge" />
            <strong>{person2?.name || 'Unknown'}</strong>
          </button>
        </header>

        <div className="graph-edge-title-row">
          <div>
            <span>Connection</span>
            <h2>{person1?.name || 'Someone'} met {person2?.name || 'someone'}</h2>
          </div>
          <strong className="graph-detail-category" style={{ '--edge-category-color': categoryColor }}>
            {getEdgeCategoryLabel(selectedEdge?.category)}
          </strong>
        </div>

        <div className="graph-detail-meta">
          <span>
            <EventIcon fontSize="small" />
            {formatDate(selectedEdge?.dateMet)}
          </span>
          <span>
            <ForumIcon fontSize="small" />
            {hasInterviewAnswers ? `${selectedEdge.interviewQuestions.length} Q&A` : 'No Q&A yet'}
          </span>
        </div>

        <article className="graph-edge-story">
          <span>Story</span>
          <p>{selectedEdge?.notes || 'No story has been added for this connection yet.'}</p>
        </article>

        <div className="graph-detail-section-header">
          <span>Interview</span>
          <strong>What they said</strong>
        </div>

        <div className="graph-edge-qa-list">
          {hasInterviewAnswers ? selectedEdge.interviewQuestions.map((question) => {
            const answerPerson = getQuestionPerson(question, person1, person2);
            return (
              <article
                key={`${question.personId}-${question.question}`}
                className="graph-edge-qa-card"
                style={{ '--detail-accent': getColor(answerPerson?._id || question.personId) }}
              >
                <h3>{question.question}</h3>
                <div className="graph-edge-answer">
                  <PersonPhoto person={answerPerson} className="graph-detail-avatar--answer" />
                  <div>
                    <strong>{answerPerson?.name || 'Answered'}</strong>
                    <p>{question.answer}</p>
                  </div>
                </div>
              </article>
            );
          }) : (
            <div className="graph-detail-empty">
              No interview questions have been added for this story yet.
            </div>
          )}
        </div>
      </section>
    </SwipeableDrawer>
  );
};
